import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { includes } from 'lodash';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSortAlt } from '@fortawesome/pro-solid-svg-icons';
import { faInfoCircle, faSortAmountUp, faSortAmountDown } from '@fortawesome/pro-solid-svg-icons';

import SelectableRecordTable from 'components/selectable_record_table/selectable_record_table';
import Row from './row';
import {
  COLUMN_DEFINITIONS as COLUMN_DEFINITIONS_ORIG,
  NAME,
  REFERRAL_SOURCE
} from './cell';
import OverlayButton from 'components/table_header/overlay_button';
import ColumnSelector from 'components/table_header/column_selector';
import Tooltip from 'components/tooltip';
import Snackbar from 'shared/snackbar';

// Used by both leads index & project leads. TODO: move to a "common" or "util" folder for clarity
export default function Table({
  cellOverrides,
  clientReportActive = false,
  columns,
  defaultColumns,
  leads,
  headerOverrides = {},
  renderLeadOptions,
  selectableLeadRowProps,
  selectAllChecked,
  sortableColumns,
  sortColumn,
  sortDesc,
  stickyColumns,
  onSelectAllToggle,
  onSetColumns,
  onSort // called with a column key (cell.js)
}) {
  //removing referralSource tooltip if client report feature
  //and client report 2.0 customization are not enabled
  const COLUMN_DEFINITIONS = !clientReportActive ? {
    ...COLUMN_DEFINITIONS_ORIG,
    [REFERRAL_SOURCE]: {
      ...COLUMN_DEFINITIONS_ORIG[REFERRAL_SOURCE],
      tooltip: undefined
    }
  } : COLUMN_DEFINITIONS_ORIG;

  const [showScrollBorder, setShowScrollBorder] = useState(false);
  const [showColumnFilters, setShowColumnFilters] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);

  const hideOverlayFilters = () => setShowColumnFilters(false);
  const showOverlayFilters = () => setShowColumnFilters(true);

  const removeInvalidColumns = () => {
    return Object.keys(columns).reduce((columnsToKeep, column) => {
      if (COLUMN_DEFINITIONS[column]) columnsToKeep[column] = columns[column];

      return columnsToKeep;
    }, {});
  };

  const listActiveColumns = () => {
    const activeColumns = {};
    const filteredColumns = removeInvalidColumns();
    Object.entries(filteredColumns).forEach(([column, isDisplayed]) => {
      if (isDisplayed) {
        activeColumns[column] = true;
      }
    });

    return activeColumns;
  };

  const allColumns = { [NAME]: true, ...listActiveColumns() };

  const columnHeaders = Object.keys(allColumns).map((columnName) => {
    let sortIcon;
    const columnDefinition = COLUMN_DEFINITIONS[columnName];
    const active = sortColumn && sortColumn === columnName;
    const ableToSort = (!sortableColumns || includes(sortableColumns, columnName)) &&
                       columnDefinition.canSort();
    const tooltip = columnDefinition.tooltip;

    if (sortColumn && ableToSort) {
      let faIcon = faSortAlt;
      if (active) faIcon = sortDesc ? faSortAmountDown : faSortAmountUp;

      sortIcon = <FontAwesomeIcon className="ml-2" icon={faIcon}/>;
    }

    return (
      <th
        className={classNames('text-nowrap', { clickable: !!sortIcon, active })}
        key={columnName}
        onClick={ableToSort ? () => onSort(columnName) : undefined}
      >
        {headerOverrides[columnName] || columnDefinition.title}
        {tooltip &&
          <Tooltip
            content={tooltip}
            dark
            placement="bottom"
          >
            <FontAwesomeIcon className="ml-2" icon={faInfoCircle}/>
          </Tooltip>}
        {sortIcon}
      </th>
    );
  });

  const columnSelectorMapping = {};
  Object.entries(defaultColumns).forEach(([column, shouldDisplay]) => {
    if (COLUMN_DEFINITIONS[column] && shouldDisplay) {
      columnSelectorMapping[column] = COLUMN_DEFINITIONS[column].title;
    }
  });

  function columnsSet(columnsToSet) {
    setShowSnackbar(true);
    onSetColumns(columnsToSet);
    hideOverlayFilters();
  }

  columnHeaders.push(
    <th className="text-center w-10-px" key="settings">
      <OverlayButton
        hide={!showColumnFilters}
        useNewIcon={true}
        onClose={hideOverlayFilters}
        onOpen={showOverlayFilters}
      >
        <ColumnSelector
          currentColumns={columns}
          defaultColumns={defaultColumns}
          informationMappings={
            {
              leadInformation: {
                mappings: columnSelectorMapping,
                key: 'lead-information'
              }
            }
          }
          onCancelColumns={hideOverlayFilters}
          onSetColumns={columnsSet}
        />
      </OverlayButton>
    </th>
  );

  const handleHorizontalScroll = (event) => {
    const shouldShowScrollBorder = event.target.scrollLeft !== 0;

    if (shouldShowScrollBorder !== showScrollBorder) setShowScrollBorder(shouldShowScrollBorder);
  };

  return (
    <React.Fragment>
      <Snackbar
        show={showSnackbar}
        text="Table columns have been applied"
        variant="success"
        onClose={() => setShowSnackbar(false)}
      />
      <div className="leads-table">
        <div className="table-responsive w-100" name="scroll-container" onScroll={handleHorizontalScroll}>
          <SelectableRecordTable
            className={`m-0 ds-table ds-table--borderless`}
            columnHeaders={columnHeaders}
            hasSelectAll={!!onSelectAllToggle}
            hasSelectColumn={!!onSelectAllToggle}
            selectAllChecked={selectAllChecked}
            stickyColShadow={showScrollBorder}
            stickyColumns={stickyColumns}
            stickyLastColumn
            onSelectAllToggle={onSelectAllToggle}
          >
            {leads.map((lead) => {
              return (
                <Row
                  cellOverrides={cellOverrides}
                  columns={Object.keys(allColumns)}
                  key={lead.id}
                  lead={lead}
                  name={`row-${lead.id}`}
                  renderLeadOptions={renderLeadOptions}
                  selectableLeadRowProps={selectableLeadRowProps}
                />
              );
            })}
          </SelectableRecordTable>
        </div>
      </div>
    </React.Fragment>
  );
}

Table.propTypes = {
  cellOverrides: PropTypes.object,
  clientReportActive: PropTypes.bool,
  columns: PropTypes.object.isRequired,
  defaultColumns: PropTypes.object.isRequired,
  headerOverrides: PropTypes.object,
  leads: PropTypes.arrayOf(PropTypes.object).isRequired,
  renderLeadOptions: PropTypes.func.isRequired,
  selectableLeadRowProps: PropTypes.func.isRequired,
  selectAllChecked: PropTypes.bool,
  sortableColumns: PropTypes.arrayOf(PropTypes.string),
  sortColumn: PropTypes.string,
  sortDesc: PropTypes.bool,
  stickyColumns: PropTypes.number,
  onSelectAllToggle: PropTypes.func,
  onSetColumns: PropTypes.func.isRequired,
  onSort: PropTypes.func
};
